import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  ChevronDown, Eye, GitCompare, Terminal, FolderOpen, ListChecks, NotebookPen, Check
} from 'lucide-react';
import { useUIStore } from '@/lib/store/uiStore';
import { cn } from '@/lib/utils';
import { chord } from '@/lib/platform';

type Mode = 'preview' | 'diff' | 'terminal' | 'files' | 'tasks' | 'plan';

const MODES: Array<{
  value: Mode;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  shortcut: string;
}> = [
  { value: 'preview',  label: 'Preview',  icon: Eye,          shortcut: chord('mod', '1') },
  { value: 'diff',     label: 'Diff',     icon: GitCompare,   shortcut: chord('mod', '2') },
  { value: 'terminal', label: 'Terminal', icon: Terminal,     shortcut: chord('mod', '3') },
  { value: 'files',    label: 'Files',    icon: FolderOpen,   shortcut: chord('mod', '4') },
  { value: 'tasks',    label: 'Tasks',    icon: ListChecks,   shortcut: chord('mod', '5') },
  { value: 'plan',     label: 'Plan',     icon: NotebookPen,  shortcut: chord('mod', '6') },
];

const LABEL: Record<Mode, string> = {
  preview: 'Preview',
  diff: 'Diff',
  terminal: 'Terminal',
  files: 'Files',
  tasks: 'Tasks',
  plan: 'Plan',
};

export function ModeMenu() {
  const mode = useUIStore((s) => s.mode);
  const setMode = useUIStore((s) => s.setMode);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="focus-ring flex items-center gap-1 rounded-md px-2 py-1 text-[12.5px] text-fg-muted hover:bg-surface-2 hover:text-fg">
          <span className="font-medium">{LABEL[mode]}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[210px] rounded-lg border border-border bg-canvas p-1 shadow-menu animate-fade-in"
        >
          {MODES.map((m) => {
            const Icon = m.icon;
            const selected = m.value === mode;
            return (
              <DropdownMenu.Item
                key={m.value}
                className="menu-item"
                onSelect={() => setMode(m.value)}
              >
                <Icon className={cn('h-3.5 w-3.5', selected ? 'text-accent' : 'text-fg-muted')} />
                <span className="flex-1">{m.label}</span>
                {selected && <Check className="h-3 w-3 text-accent" />}
                <kbd className="ml-2">{m.shortcut}</kbd>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
